/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from "react-i18next";

import OutcomeImages from 'shared/components/outcomes/OutcomeImages';
import { useTicketSlipStore } from 'features/tickets/store';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { getMarketTypeByGroupId } from 'shared/utils/markets';
import { pickOutcome } from 'shared/utils/ticket-slip-helper';
import { cn } from 'lib/utils';
import { getSportColor, getSportTextColorClasses } from 'shared/utils/sport-colors';

/**
 * Renders the tricast markets with selection flow
 *
 * @class      TricastMarkets (name)
 * @param      {<type>}   props   The properties
 * @return     {boolean}  { description_of_the_return_value }
 */
function TricastMarkets(props) {
  const { t } = useTranslation();
  const {
    available_market_groups = [],
    event = {},
    odds = {},
    sport_id: propSportId,
  } = props;

  const { n_runners = 6, event_id, odds: eventOdds = {} } = event;
  // Extract sport_id from props first, then from event, with proper null/undefined checking
  const sportObj = event?.sport;
  const sport_id = propSportId ?? sportObj?.sport_id ?? event?.sport_id;
  if (sport_id == null) return null;

  // Use odds from props first, then from event
  const finalOdds = Object.keys(odds).length > 0 ? odds : eventOdds;

  // Get sport-specific color and text classes
  const sportColor = getSportColor(sport_id);
  const textColorSport = getSportTextColorClasses(sport_id);

  // Check if tricast markets are available
  const hasStraightTricast = available_market_groups.includes(506);
  const hasReverseTricast = available_market_groups.includes(507);

  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const addTip = useTicketSlipStore((s) => s.addTip);
  const selected_event = useScheduleContext().selectedEvent || {};
  const eventForOutcomes = event?.event_id != null ? event : selected_event;

  // Selection state
  const [firstPlace, setFirstPlace] = React.useState(null);
  const [secondPlace, setSecondPlace] = React.useState(null);
  const [thirdPlace, setThirdPlace] = React.useState(null);
  const [orderType, setOrderType] = React.useState('straight'); // 'straight' or 'reverse'

  // Generate runners array
  const runners = Array.from({ length: n_runners }, (_, i) => i + 1);

  // Check if selection is complete
  const isSelectionComplete = firstPlace && secondPlace && thirdPlace && orderType;

  // Handle runner selection
  const handleRunnerSelect = (position, runner) => {
    if (position === 'first') {
      setFirstPlace(runner);
      // Reset subsequent selections if needed
      if (secondPlace === runner) setSecondPlace(null);
      if (thirdPlace === runner) setThirdPlace(null);
    } else if (position === 'second') {
      if (runner === firstPlace) return; // Can't select same runner
      setSecondPlace(runner);
      if (thirdPlace === runner) setThirdPlace(null);
    } else if (position === 'third') {
      if (runner === firstPlace || runner === secondPlace) return; // Can't select same runner
      setThirdPlace(runner);
    }
  };

  // Add to betslip
  const addToBetslip = () => {
    if (!isSelectionComplete) return;

    const market_type_group_id = orderType === 'straight' ? 506 : 507;
    const marketTypeData = getMarketTypeByGroupId(market_type_group_id, props) ?? {};
    const market_type_id = marketTypeData.market_type_id;
    const market_name = marketTypeData.name || '';
    const outcome = `${firstPlace}-${secondPlace}-${thirdPlace}`;

    const outcomesList = Array.isArray(eventForOutcomes?.outcomes)
      ? eventForOutcomes.outcomes
      : eventForOutcomes?.outcomes && typeof eventForOutcomes.outcomes === 'object'
        ? Object.values(eventForOutcomes.outcomes)
        : [];
    const outcomeObj =
      outcomesList.find((o) => String(o?.market_type_id) === String(market_type_id) && String(o?.outcome) === outcome) || null;
    // Prefer outcome.odd (new payload shape), but keep legacy odds map as fallback.
    const odd =
      outcomeObj?.odd ??
      (finalOdds?.[market_type_id] && finalOdds[market_type_id][outcome] ? finalOdds[market_type_id][outcome] : null);

    // Don't add if no odd is available
    if (!odd) {
      console.warn('TricastMarkets: No odd available for outcome', outcome, 'in market', market_type_id);
      return;
    }

    // Try to use pickOutcome to get outcome data from event.outcomes
    // If it fails (returns false), we'll create a manual tip object
    let tipData = pickOutcome({
      event: eventForOutcomes,
      pick_outcome: outcome,
      market_type_id: market_type_id,
    });

    // If pickOutcome failed, create manual tip object with required fields
    if (!tipData) {
      // Find outcome_id by searching in event.outcomes array
      const outcomes = eventForOutcomes.outcomes || [];
      let outcome_id = outcomes.findIndex(
        (item, index) =>
          item.outcome?.toString() === outcome.toString() &&
          parseInt(item.market_type_id) === parseInt(market_type_id)
      );

      // If still not found, generate a unique outcome_id based on event_id, market_type_id, and outcome
      if (outcome_id < 0) {
        outcome_id = `${event_id}_${market_type_id}_${outcome}`;
      }

      tipData = {
        outcome_id: outcomeObj?.outcome_id ?? outcome_id,
        event_id: event_id,
        sport_id: eventForOutcomes.sport_id || sport_id,
        n_runners: eventForOutcomes.n_runners || n_runners,
        description: eventForOutcomes.description || '',
        outcome: outcome,
        odd: odd,
        market_type_id: market_type_id,
      };
    }

    // Build complete tip object
    const tip = {
      ...tipData,
      stake: 1.00,
      market_type_group_id: market_type_group_id,
      market_name: market_name,
      odd: odd, // Ensure odd is set
    };

    // Check if already in ticket using outcome_id
    const isInTicket = ticket_slip.some(item =>
      item.outcome_id === tip.outcome_id ||
      (item.event_id === event_id &&
       item.market_type_group_id === market_type_group_id &&
       item.outcome === outcome)
    );

    if (isInTicket) return;

    // Add to ticket slip
    addTip(tip);

    // Reset selections
    setFirstPlace(null);
    setSecondPlace(null);
    setThirdPlace(null);
    setOrderType('straight');
  };

  if (!hasStraightTricast && !hasReverseTricast) {
    return (
      <div className="tricast-markets p-4 text-center text-gray-500">
        <p>{t('NO_TRICAST_MARKETS_AVAILABLE')}</p>
      </div>
    );
  }

  // Generate unique component ID that updates with sport_id to ensure CSS is regenerated
  const componentId = React.useMemo(() => `tricast-${sport_id}-${Math.random().toString(36).substr(2, 9)}`, [sport_id]);

  // Generate CSS for hover states - MUST use sportColor variable, not hardcoded
  const hoverStyles = React.useMemo(() => {
    // Double-check sportColor is correct
    const actualSportColor = getSportColor(sport_id);
    return `
      #${componentId} button.tricast-runner-button:hover:not([data-selected="true"]):not(:disabled),
      #${componentId} button.tricast-runner-button:hover:not([aria-pressed="true"]):not(:disabled) {
        border-color: ${actualSportColor} !important;
        background-color: ${actualSportColor} !important;
        background: ${actualSportColor} !important;
        color: white !important;
      }
      #${componentId} button.tricast-order-button:hover:not([data-selected="true"]):not(:disabled),
      #${componentId} button.tricast-order-button:hover:not([aria-pressed="true"]):not(:disabled) {
        border-color: ${actualSportColor} !important;
        background-color: ${actualSportColor} !important;
        background: ${actualSportColor} !important;
        color: white !important;
      }
      #${componentId} button.tricast-runner-button[data-selected="true"],
      #${componentId} button.tricast-runner-button[aria-pressed="true"] {
        border-color: ${actualSportColor} !important;
        background-color: ${actualSportColor} !important;
        background: ${actualSportColor} !important;
        color: white !important;
      }
      #${componentId} button.tricast-order-button[data-selected="true"],
      #${componentId} button.tricast-order-button[aria-pressed="true"] {
        border-color: ${actualSportColor} !important;
        background-color: ${actualSportColor} !important;
        background: ${actualSportColor} !important;
        color: white !important;
      }
    `;
  }, [componentId, sport_id]);

  return (
    <div id={componentId} className="tricast-markets w-full p-4" data-sport-id={sport_id} style={{ '--sport-color': sportColor }}>
      <style>{hoverStyles}</style>
      <div className="space-y-4">
        {/* First Place Selection */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-foreground">{t('SELECT_FIRST_PLACE')}</h4>
          <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${n_runners}, minmax(0, 1fr))` }}>
            {runners.map((runner) => (
              <button
                key={runner}
                type="button"
                aria-pressed={firstPlace === runner}
                data-selected={firstPlace === runner}
                className={cn(
                  "tricast-runner-button flex items-center justify-center p-2 border rounded transition-all",
                  firstPlace === runner
                    ? `${textColorSport} ring-2 ring-offset-1`
                    : "border-border bg-background text-foreground"
                )}
                style={{
                  ...(firstPlace === runner ? {
                    borderColor: sportColor,
                    backgroundColor: sportColor,
                    color: 'white',
                    '--ring-color': sportColor
                  } : {})
                }}
                onClick={() => handleRunnerSelect('first', runner)}
              >
                <OutcomeImages {...{outcome: runner, n_runners, sport_id, br: 0}}/>
              </button>
            ))}
          </div>
        </div>

        {/* Second Place Selection */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900">{t('SELECT_SECOND_PLACE')}</h4>
          <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${n_runners}, minmax(0, 1fr))` }}>
            {runners.map((runner) => (
              <button
                key={runner}
                type="button"
                aria-pressed={secondPlace === runner}
                data-selected={secondPlace === runner}
                className={cn(
                  "tricast-runner-button flex items-center justify-center p-2 border rounded transition-all",
                  secondPlace === runner
                    ? `${textColorSport} ring-2 ring-offset-1`
                    : firstPlace === runner
                    ? "border-border bg-muted text-muted-foreground cursor-not-allowed"
                    : "border-border bg-background text-foreground"
                )}
                style={{
                  ...(secondPlace === runner ? {
                    borderColor: sportColor,
                    backgroundColor: sportColor,
                    color: 'white',
                    '--ring-color': sportColor
                  } : {})
                }}
                onClick={() => handleRunnerSelect('second', runner)}
                disabled={firstPlace === runner}
              >
                <OutcomeImages {...{outcome: runner, n_runners, sport_id, br: 0}}/>
              </button>
            ))}
          </div>
        </div>

        {/* Third Place Selection */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900">{t('SELECT_THIRD_PLACE')}</h4>
          <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${n_runners}, minmax(0, 1fr))` }}>
            {runners.map((runner) => (
              <button
                key={runner}
                type="button"
                aria-pressed={thirdPlace === runner}
                data-selected={thirdPlace === runner}
                className={cn(
                  "tricast-runner-button flex items-center justify-center p-2 border rounded transition-all",
                  thirdPlace === runner
                    ? `${textColorSport} ring-2 ring-offset-1`
                    : firstPlace === runner || secondPlace === runner
                    ? "border-border bg-muted text-muted-foreground cursor-not-allowed"
                    : "border-border bg-background text-foreground"
                )}
                style={{
                  ...(thirdPlace === runner ? {
                    borderColor: sportColor,
                    backgroundColor: sportColor,
                    color: 'white',
                    '--ring-color': sportColor
                  } : {})
                }}
                onClick={() => handleRunnerSelect('third', runner)}
                disabled={firstPlace === runner || secondPlace === runner}
              >
                <OutcomeImages {...{outcome: runner, n_runners, sport_id, br: 0}}/>
              </button>
            ))}
          </div>
        </div>

        {/* Order Type Selection */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900">{t('SELECT_ORDER_TYPE')}</h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              className={cn(
                "tricast-order-button p-3 border rounded transition-all text-center",
                orderType === 'straight'
                  ? `${textColorSport}`
                  : "border-gray-300 bg-white text-gray-700"
              )}
              data-selected={orderType === 'straight'}
              style={{
                ...(orderType === 'straight' ? {
                  borderColor: sportColor,
                  backgroundColor: sportColor,
                  color: 'white'
                } : {})
              }}
              onClick={() => setOrderType('straight')}
            >
              <div className="flex items-center justify-center gap-2">
                <i className="bi bi-arrow-right text-lg"></i>
                <div className="text-sm font-semibold">{t('IN_ORDER')}</div>
              </div>
              <div className="text-xs opacity-75 mt-1">{t('STRAIGHT_TRICAST')}</div>
            </button>
            <button
              className={cn(
                "tricast-order-button p-3 border rounded transition-all text-center",
                orderType === 'reverse'
                  ? `${textColorSport}`
                  : "border-gray-300 bg-white text-gray-700"
              )}
              data-selected={orderType === 'reverse'}
              style={{
                ...(orderType === 'reverse' ? {
                  borderColor: sportColor,
                  backgroundColor: sportColor,
                  color: 'white'
                } : {})
              }}
              onClick={() => setOrderType('reverse')}
            >
              <div className="flex items-center justify-center gap-2">
                <i className="bi bi-arrow-left-right text-lg"></i>
                <div className="text-sm font-semibold">{t('NOT_IN_ORDER')}</div>
              </div>
              <div className="text-xs opacity-75 mt-1">{t('REVERSE_TRICAST')}</div>
            </button>
          </div>
        </div>

        {/* Add to Betslip Button */}
        {isSelectionComplete && (
          <button
            className={`w-full p-3 ${textColorSport} rounded-lg font-semibold transition-colors`}
            style={{
              backgroundColor: sportColor,
              color: 'white'
            }}
            onClick={addToBetslip}
          >
            {t('ADD_TO_BETSLIP')} - {firstPlace}, {secondPlace}, {thirdPlace} ({t(orderType === 'straight' ? 'IN_ORDER' : 'NOT_IN_ORDER')})
          </button>
        )}
      </div>
    </div>
  );
}

export default TricastMarkets;

